import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MobileTopBar } from 'app/components/MobileTopBar'
import { Sheet, SheetContent, SheetTitle } from 'app/components/ui/sheet'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { cn } from 'app/lib/utils'
import { CloseNavContext } from 'app/hooks/useCloseNav'
import { MainGutterContext } from 'app/hooks/useMainGutter'

type Props = {
  sidebar: React.ReactNode
  children: React.ReactNode
}

// Pages that manage their own edge-to-edge padding (inbox chat, ops tools).
const NO_PADDING_PAGES = [/^\/inbox/, /^\/ops/, /^\/workflows\/.+/, /^\/action-center\/work\//]

// Routes that opt into full-bleed edge scrolling via <PageScroll>. `main` drops
// its gutter (the wrapper re-adds it internally) so the scroll region can reach
// the window edge. overflow-hidden stays untouched.
const EDGE_SCROLL_PAGES = [
  // Top-level pages
  /^\/action-center$/,
  /^\/analytics\//,
  /^\/contacts/,
  /^\/treatment-plans/,
  /^\/appointments(?:\/|$)/,
  /^\/appointments-calendar$/,
  // List pages: the board and table views draw through <PageScroll>, which re-adds the
  // gutter, and the Card view fills the main area edge to edge like the workflow canvas.
  // Without this every list page sat 40px further in and 32px lower than Contacts.
  /^\/lists\//,
  // The Pipelines page: its three system pipelines draw Action Center boards
  // through <PageScroll>, which re-adds the gutter itself. Without this opt-in
  // `main`'s gutter stacked on top of it and those boards sat 40px further in
  // than every other page's header, on the same page as the ordinary pipelines
  // that did not. The non-system branch pads itself, see PipelinesComponent.
  /^\/pipelines/,
  /^\/activities/,
  /^\/tasks/,
  /^\/scorecards/,
  /^\/import$/,
  /^\/calls(\/|$)/,
  /^\/agents(\/|$)/,
  /^\/workflows$/,
  /^\/companies\/(locations|workspaces)/,
  // AI Agent (app-side)
  /^\/ai-agent\/call-log/,
  /^\/ai-agent\/configuration/,
  /^\/ai-agent\/voice/,
  // Settings — standalone list/table + page-scroll pages
  /^\/settings\/workspace$/,
  /^\/settings\/conversion-monitoring/,
  /^\/settings\/roles/,
  /^\/settings\/staff/,
  /^\/settings\/feature-toggles/,
  /^\/settings\/templates/,
  /^\/settings\/imports/,
  /^\/settings\/call-data-extraction$/,
  /^\/settings\/ai-agent$/,
  // Settings — section index (list) pages ($-anchored so they don't match the
  // `:id/child` detail subtrees below)
  /^\/settings\/users(\/(active|invited))?$/,
  /^\/settings\/location-settings$/,
  /^\/settings\/scheduling$/,
  /^\/settings\/availability$/,
  /^\/settings\/online-scheduler$/,
  // Settings — `:id/child` detail-shell subtrees (trailing slash: only children,
  // not the index/list page above)
  /^\/settings\/users\/[^/]+\//,
  /^\/settings\/location-settings\/[^/]+\//,
  /^\/settings\/scheduling\/[^/]+\//,
  /^\/settings\/availability\/[^/]+\//,
  /^\/settings\/ai-agent\/[^/]+\//,
  /^\/settings\/online-scheduler\/[^/]+/,
  // Settings — shared-header tab shells (each hosts its own tab children)
  /^\/settings\/mapping\//,
  /^\/settings\/referral-types\//,
  /^\/settings\/integrations\//,
  /^\/settings\/tags\//,
]

/**
 * Presentational app shell. Owns ONLY the responsive layout:
 *  - desktop (>= md): sidebar sits inline in a row.
 *  - mobile  (<  md): sidebar collapses into a left-anchored drawer opened from
 *    a MobileTopBar hamburger.
 * The `sidebar` node is rendered exactly once (inline OR in the drawer), never
 * both. All data/effects live in the AppLayout wrapper. Keystone rule: reflow,
 * never fork — see docs/mobile-responsive-patterns.md.
 */
export function AppShell({ sidebar, children }: Props): React.ReactElement {
  const isMobile = useIsMobile()
  const location = useLocation()
  const [navOpen, setNavOpen] = useState(false)
  const [gutterStripped, setGutterStripped] = useState(false)

  // Close the drawer on every navigation. Keying on location.key (which React
  // Router changes on each navigation) also catches query-string/hash-only
  // changes and same-path re-navigations that location.pathname would miss.
  useEffect(() => {
    setNavOpen(false)
  }, [location.key])

  // Reset the drawer when leaving mobile, so crossing back under md doesn't
  // auto-open it from stale open state.
  useEffect(() => {
    if (!isMobile) setNavOpen(false)
  }, [isMobile])

  // Strip main's gutter for pages that own their padding (inbox/ops/workflows)
  // and for edge-scroll pages whose <PageScroll> re-adds the gutter internally.
  const noPad = gutterStripped || [...NO_PADDING_PAGES, ...EDGE_SCROLL_PAGES].some((re) => re.test(location.pathname))
  const closeNav = useCallback(() => setNavOpen(false), [])

  return (
    <CloseNavContext.Provider value={closeNav}>
      <MainGutterContext.Provider value={setGutterStripped}>
        <div id="skeleton" className="flex h-dvh flex-col md:h-screen md:flex-row">
          {isMobile ? <MobileTopBar onOpenNav={() => setNavOpen(true)} navOpen={navOpen} /> : sidebar}
          <main className={cn('relative min-h-0 flex-1 overflow-hidden', noPad ? '' : 'px-4 pt-4 md:px-10 md:pt-8')}>
            <div className="flex h-full max-h-full flex-col">
              <div className="outlet h-full max-h-full grow">{children}</div>
            </div>
          </main>
        </div>

        {/*
          No `modal` prop -> sheet.tsx's default modal={false}. Radix modal mode
          sets pointer-events:none on document.body, which would swallow clicks
          inside the Headless-UI global Modal that SideBarV2's "New List"/"New
          Pipeline" actions open via showModal(). The SheetOverlay still renders
          and Esc/scrim-click still close via DismissableLayer.
        */}
        {isMobile && (
          <>
            {/*
              SheetContent's built-in SheetOverlay renders null in non-modal mode
              (Radix's Dialog.Overlay is a no-op when modal={false}), so we render
              our own plain dimmed backdrop here. It's a normal div, not Radix
              modal mode, so it does NOT set pointer-events:none on body: the
              Headless-UI "New List"/"New Pipeline" modals opened from inside the
              drawer keep working. z-20 sits above page content but below the
              z-30 SheetContent drawer.
            */}
            {navOpen && (
              <div
                data-testid="nav-backdrop"
                className="fixed inset-0 z-20 bg-overlay/40"
                aria-hidden="true"
                onClick={() => setNavOpen(false)}
              />
            )}
            <Sheet open={navOpen} onOpenChange={setNavOpen}>
              <SheetContent id="app-nav-drawer" side="left" className="w-[280px] p-0" aria-describedby={undefined}>
                {/* Radix Dialog requires a title for a11y; the nav is self-evident. */}
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                {sidebar}
              </SheetContent>
            </Sheet>
          </>
        )}
      </MainGutterContext.Provider>
    </CloseNavContext.Provider>
  )
}
